import { UploadChunkRequest, UploadChunkReply } from './file_transfer_pb';

export const encodeVarint = (value: number): Uint8Array => {
    const result: number[] = [];

    while (value >= 0x80) {
        result.push((value & 0xff) | 0x80);
        value >>>= 7;
    }
    result.push(value & 0xff);

    return new Uint8Array(result);
};

export const decodeVarint = (data: Uint8Array, pos: number): [number, number] => {
    let result = 0;
    let shift = 0;

    while (pos < data.length) {
        const byte = data[pos++];
        result |= (byte & 0x7f) << shift;

        if ((byte & 0x80) === 0) {
            break;
        }

        shift += 7;
        if (shift >= 32) {
            throw new Error('Varint too long');
        }
    }

    return [result, pos];
};

export const skipProtobufField = (data: Uint8Array, pos: number, wireType: number): number => {
    switch (wireType) {
        case 0: {
            const [, newPos] = decodeVarint(data, pos);
            return newPos;
        }
        case 1:
            return pos + 8;
        case 2: {
            const [length, lengthPos] = decodeVarint(data, pos);
            return lengthPos + length;
        }
        case 5:
            return pos + 4;
        default:
            return data.length;
    }
};

const readLengthDelimitedString = (data: Uint8Array, pos: number): [string, number] => {
    const [length, lengthPos] = decodeVarint(data, pos);
    const nextPos = lengthPos + length;
    const bytes = data.slice(lengthPos, nextPos);
    return [new TextDecoder().decode(bytes), nextPos];
};

const applyUploadChunkReplyField = (
    result: UploadChunkReply,
    data: Uint8Array,
    pos: number,
    fieldNumber: number,
    wireType: number
): number => {
    if (fieldNumber === 1 && wireType === 0) {
        const [value, nextPos] = decodeVarint(data, pos);
        result.completed = value !== 0;
        return nextPos;
    }
    if (fieldNumber === 2 && wireType === 2) {
        const [message, nextPos] = readLengthDelimitedString(data, pos);
        result.message = message;
        return nextPos;
    }
    if (fieldNumber === 3 && wireType === 2) {
        const [path, nextPos] = readLengthDelimitedString(data, pos);
        result.fileUploadPath = path;
        return nextPos;
    }
    return skipProtobufField(data, pos, wireType);
};

export const decodeUploadChunkReplyMessage = (data: Uint8Array): UploadChunkReply => {
    const result: UploadChunkReply = {
        completed: false,
        message: '',
        fileUploadPath: '',
    };

    let pos = 0;
    while (pos < data.length) {
        const [tag, newPos] = decodeVarint(data, pos);
        pos = newPos;
        const fieldNumber = tag >>> 3;
        const wireType = tag & 0x7;
        pos = applyUploadChunkReplyField(result, data, pos, fieldNumber, wireType);
    }

    return result;
};

export const decodeUploadChunkReplyFrame = (buffer: ArrayBuffer): UploadChunkReply => {
    try {
        let offset = 0;
        let messageLength = buffer.byteLength;

        if (buffer.byteLength >= 5) {
            const view = new DataView(buffer);
            const compressionFlag = view.getUint8(0);
            const frameMsgLength = view.getUint32(1, false);

            if (compressionFlag === 0 && frameMsgLength > 0 && frameMsgLength <= buffer.byteLength - 5) {
                offset = 5;
                messageLength = frameMsgLength;
            }
        }

        const messageBytes = new Uint8Array(buffer, offset, messageLength);
        return decodeUploadChunkReplyMessage(messageBytes);
    } catch {
        return {
            completed: true,
            message: 'Upload completed (binary)',
            fileUploadPath: '',
        };
    }
};

const appendStringField = (chunks: Uint8Array[], fieldTag: number, value: string) => {
    const valueBytes = new TextEncoder().encode(value);
    chunks.push(encodeVarint(fieldTag));
    chunks.push(encodeVarint(valueBytes.length));
    chunks.push(valueBytes);
};

const appendVarintField = (chunks: Uint8Array[], fieldTag: number, value: number) => {
    chunks.push(encodeVarint(fieldTag));
    chunks.push(encodeVarint(value));
};

const appendBoolField = (chunks: Uint8Array[], fieldTag: number, value: boolean) => {
    appendVarintField(chunks, fieldTag, value ? 1 : 0);
};

export const encodeUploadChunkRequestFields = (request: UploadChunkRequest): Uint8Array => {
    const chunks: Uint8Array[] = [];

    if (request.fileName) {
        appendStringField(chunks, 0x0a, request.fileName);
    }

    if (request.chunkIndex !== undefined) {
        appendVarintField(chunks, 0x10, request.chunkIndex);
    }

    if (request.data && request.data.length > 0) {
        chunks.push(encodeVarint(0x1a));
        chunks.push(encodeVarint(request.data.length));
        chunks.push(request.data);
    }

    if (request.isLastChunk !== undefined) {
        appendBoolField(chunks, 0x20, request.isLastChunk);
    }

    if (request.key) {
        appendStringField(chunks, 0x2a, request.key);
    }

    if (request.totalChunks !== undefined && request.totalChunks > 0) {
        appendVarintField(chunks, 0x30, request.totalChunks);
    }

    if (request.fileTransferRequestId !== undefined && request.fileTransferRequestId > 0) {
        appendVarintField(chunks, 0x38, request.fileTransferRequestId);
    }

    if (request.fileTransferRequestDetailId !== undefined && request.fileTransferRequestDetailId > 0) {
        appendVarintField(chunks, 0x40, request.fileTransferRequestDetailId);
    }

    if (request.priority !== undefined && request.priority > 0) {
        appendVarintField(chunks, 0x48, request.priority);
    }

    if (request.isLastFileInBatch !== undefined) {
        appendBoolField(chunks, 0x50, request.isLastFileInBatch);
    }

    if (request.batchFileNames && request.batchFileNames.length > 0) {
        for (const fileName of request.batchFileNames) {
            appendStringField(chunks, 0x5a, fileName);
        }
    }

    if (request.folderPrefix) {
        appendStringField(chunks, 0x62, request.folderPrefix);
    }

    if (request.userName) {
        appendStringField(chunks, 0x6a, request.userName);
    }

    const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;

    for (const chunk of chunks) {
        result.set(chunk, offset);
        offset += chunk.length;
    }

    return result;
};
